import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Icon,
} from "@sellar-io/components";

import { useState } from "react";

import { formatDuration } from "@sellar-io/common";

import { AddAvailabilityNotificationDialog } from "./AddAvailabilityNotificationDialog";

import type { StorefrontAvailabilityNotificationListResponseType } from "@sellar-io/api-v2/types";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { Alert } from "../../../../components/alert/Alert";

import { useSupplierStorefrontAvailabilityNotificationDelete } from "../../../../hooks/api/v2/storefrontAvailability/notifications/useSupplierStorefrontAvailabilityNotificationDelete";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";

export type AvailabilityAlertNotification = {
  id: number;
  triggerOffsetMs: number;
  type: "email" | "app";
  intervalMarker: "start" | "end";
};

export const AvailabilityNotificationDeleteDialog = ({
  notificationId,
  supplierId,
  onDismiss,
  active,
}: {
  notificationId: number;
  supplierId: number;
  onDismiss: () => void;
  active: boolean;
}) => {
  const notificationDeleteMutation =
    useSupplierStorefrontAvailabilityNotificationDelete({
      supplierId,
      notificationId,
    });

  return (
    <Dialog active={active} onDismiss={onDismiss} className="m-4 h-fit">
      {notificationDeleteMutation.isError && (
        <Alert
          type={"error"}
          message={
            apiV2AssertError(notificationDeleteMutation.error)?.response?.data
              .message ?? "Unknown error"
          }
        />
      )}

      <DialogNavigation
        title={`Delete notification`}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />

      <DialogContent className="flex justify-center text-zinc-500">
        Are you sure you wish to delete this notification?
      </DialogContent>

      <DialogCTA>
        <Button
          color={"red"}
          onClick={() =>
            notificationDeleteMutation.mutate(undefined, {
              onSuccess: () => {
                onDismiss();
              },
            })
          }
          disabled={notificationDeleteMutation.isPending}
        >
          Delete
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

export const AvailabilityNotificationsList = ({
  availabilityNotificationList,
  intervalMarker,
  supplierId,
}: {
  availabilityNotificationList: StorefrontAvailabilityNotificationListResponseType;
  intervalMarker: "start" | "end";
  supplierId: number;
}) => {
  const [addNotificationDialogActive, setAddNotificationDialogActive] =
    useState(false);

  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge("bg-white", "flex items-center justify-between")}
        >
          <Button
            color={"secondary"}
            onClick={() => setAddNotificationDialogActive(true)}
          >
            <Icon icon={"PLUS"} />
            Add availability reminder
          </Button>
        </CardHeader>
        <CardContent className="w-full">
          {!!availabilityNotificationList.length && (
            <div className="flex items-center gap-4">
              <Icon icon="BELL_OUTLINE" />
              <div className="flex w-full flex-col">
                {availabilityNotificationList.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    supplierId={supplierId}
                    notification={notification}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddAvailabilityNotificationDialog
        active={addNotificationDialogActive}
        supplierId={supplierId}
        intervalMarker={intervalMarker}
        onDismiss={() => setAddNotificationDialogActive(false)}
      />
    </>
  );
};

const generateNotificationCopy = (
  notification: StorefrontAvailabilityNotificationListResponseType[number],
) => {
  const { triggerOffsetMs, type } = notification;
  const formattedDuration = formatDuration(triggerOffsetMs);
  const formattedType = type === "email" ? "email" : "app notification";

  if (triggerOffsetMs === 0) {
    return `When the event starts, as ${formattedType}`;
  }

  return `${formattedDuration} before, as ${formattedType}`;
};

const NotificationRow = ({
  supplierId,
  notification,
}: {
  supplierId: number;
  notification: StorefrontAvailabilityNotificationListResponseType[number];
}) => {
  const [deleteNotificationDialogActive, setDeleteNotificationDialogActive] =
    useState<boolean>(false);

  return (
    <>
      <div className="flex items-center justify-between">
        <div>{generateNotificationCopy(notification)}</div>

        <Button
          color={"transparent"}
          onClick={() => setDeleteNotificationDialogActive(true)}
          size="small"
        >
          <Icon icon={"DELETE"} />
        </Button>
      </div>

      <AvailabilityNotificationDeleteDialog
        supplierId={supplierId}
        notificationId={notification.id}
        active={deleteNotificationDialogActive}
        onDismiss={() => setDeleteNotificationDialogActive(false)}
      />
    </>
  );
};
